/**
 * EnumSelect — Layer-1 single-select dropdown driven by the localized enum API.
 *
 * Renders a `ThemedCombobox` (same visual family as the multi-select used for
 * "Distribution channels") but for one value at a time. Options come from
 * `useEnumList`, pre-localized by the server — no `t()` on the values. Shows a
 * disabled loading state while the catalog is in flight, and (when
 * `includeUnset`) a clear affordance to reset to "no value".
 */
import { useMemo } from "react";
import { ThemedCombobox } from "./ThemedCombobox";
import { useEnumList } from "@/shared/hooks/useEnums";

interface EnumSelectProps {
  /** Backend enum name (snake_case), e.g. "age_group". */
  name: string;
  value: string;
  onChange: (value: string) => void;
  /** Placeholder shown when no value is selected. */
  placeholder?: string;
  /** When true, the value can be cleared back to "" (unset). */
  includeUnset?: boolean;
  error?: boolean;
  disabled?: boolean;
  /** Type-to-filter the list (useful for long lists like languages). */
  searchable?: boolean;
  id?: string;
  className?: string;
}

export function EnumSelect({
  name,
  value,
  onChange,
  placeholder,
  includeUnset = true,
  error = false,
  disabled = false,
  searchable = false,
  id,
  className,
}: EnumSelectProps) {
  const { items, isLoading } = useEnumList(name);

  const options = useMemo(
    () => items.map((item) => ({ value: item.value, label: item.label })),
    [items],
  );

  return (
    <ThemedCombobox
      options={options}
      value={value || null}
      onChange={(v) => onChange(v ?? "")}
      {...(placeholder !== undefined ? { placeholder } : {})}
      searchable={searchable}
      clearable={includeUnset}
      error={error}
      disabled={disabled}
      loading={isLoading}
      fullWidth
      {...(id !== undefined ? { id } : {})}
      {...(className !== undefined ? { className } : {})}
    />
  );
}
